// API keys for integrations. A new key's full value is shown once; after that
// only its prefix is listed. Keys can be paused, resumed or revoked. Available
// on Basic and up (canAccessApiKeys).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { apiKeysApi, type ApiKeyCreatedResponse, type ApiKeyListItem } from '@app/api/apikeys';
import { canAccessApiKeys, getSubscription, type Subscription } from '@app/api/subscription';
import { Banner, Button, ConfirmDialog, EmptyState, ErrorState, Field, Input, Modal, ModalActions, PageHeader, SkeletonRows, StatusPill, useToast } from '@app/components/ui';
import { monthDayYear } from '@app/lib/format';
import { logger } from '@app/utils/logger';

const BASE_URL = 'https://api.churchnow.co';

export const Developer: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const churchId = user?.churchId;
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [keys, setKeys] = useState<ApiKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [created, setCreated] = useState<ApiKeyCreatedResponse | null>(null);
  const [revoke, setRevoke] = useState<ApiKeyListItem | null>(null);

  const load = async () => {
    if (!churchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    try {
      const sub = await getSubscription(churchId);
      setSubscription(sub);
      setKeys(canAccessApiKeys(sub) ? await apiKeysApi.list(churchId) : []);
    } catch (err) {
      logger.error('Failed to load developer settings', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId]);

  const act = async (key: ApiKeyListItem, kind: 'pause' | 'resume' | 'revoke') => {
    if (!churchId) return;
    setBusy(key.id);
    try {
      await apiKeysApi[kind](churchId, key.id);
      toast(kind === 'revoke' ? `${key.name} revoked` : kind === 'pause' ? `${key.name} paused` : `${key.name} resumed`);
      await load();
    } catch (err) {
      logger.error('API key action failed', err);
      toast('That didn’t work. Try again.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const allowed = canAccessApiKeys(subscription);
  const visible = keys.filter((k) => k.status !== 'Revoked');

  return (
    <div className="max-w-[860px]">
      <PageHeader
        eyebrow={
          <>
            <StatusPill tone="amb" className="!text-[11px] !tracking-[.06em] !px-2 !py-[3px]">
              BASIC AND UP
            </StatusPill>
            API access
          </>
        }
        title="Developer"
        actions={
          allowed && (
            <Button variant="dark" onClick={() => setNewOpen(true)}>
              + New API key
            </Button>
          )
        }
      />

      {loadError ? (
        <ErrorState title="Couldn't load your API keys" body="Check your connection and try again." onRetry={load} retrying={loading} />
      ) : loading ? (
        <SkeletonRows rows={2} />
      ) : !allowed ? (
        <EmptyState
          title="API keys need the Basic plan or above"
          body="Upgrade to connect Worship Planner to your other tools."
          action={
            <Button variant="dark" onClick={() => navigate('/admin/subscription')}>
              See plans
            </Button>
          }
        />
      ) : (
        <>
          {created && (
            <div className="wp-rise rounded-[20px] border bg-ok-bg p-5 mb-3.5" style={{ borderColor: 'color-mix(in srgb, var(--ok) 35%, transparent)' }} role="status">
              <div className="text-[15px] font-semibold">Copy your key now</div>
              <p className="text-[13px] text-t2 mt-1">This is the only time the full key is shown. Store it in a secrets manager.</p>
              <div className="flex flex-wrap items-center gap-2.5 mt-3">
                <code className="flex-1 min-w-0 break-all font-mono text-[13px] bg-card border border-line rounded-[12px] px-3.5 py-2.5">{created.rawKey}</code>
                <Button
                  variant="dark"
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(created.rawKey);
                      toast('Key copied');
                    } catch {
                      toast('Copy failed. Select the key and copy it manually.', 'error');
                    }
                  }}
                >
                  Copy
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCreated(null)}>
                  Done
                </Button>
              </div>
            </div>
          )}

          {visible.length === 0 ? (
            <EmptyState title="No API keys yet" body="Create one key per integration so you can revoke it on its own." />
          ) : (
            <ul className="flex flex-col gap-2.5">
              {visible.map((k) => (
                <li key={k.id} className="flex flex-wrap items-center gap-4 rounded-[20px] border border-line bg-card px-5 py-[18px]">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-semibold">{k.name}</span>
                      {k.status === 'Paused' && <StatusPill status="Paused" tone="amb" />}
                    </div>
                    <div className="font-mono text-[13px] text-t3 mt-1">{k.keyPrefix}••••••••</div>
                  </div>
                  <div className="text-[12.5px] text-t4 text-right">
                    Created {monthDayYear(k.createdAt)}
                    <br />
                    {k.requestsPerMinute} requests a minute
                  </div>
                  <div className="flex gap-2">
                    {k.status === 'Active' ? (
                      <Button variant="ghost" size="sm" className="!h-[34px] !text-[13px]" loading={busy === k.id} onClick={() => act(k, 'pause')}>
                        Pause
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="!h-[34px] !text-[13px]" loading={busy === k.id} onClick={() => act(k, 'resume')}>
                        Resume
                      </Button>
                    )}
                    <Button variant="danger-ghost" size="sm" className="!h-[34px] !text-[13px]" onClick={() => setRevoke(k)}>
                      Revoke
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Banner tone="muted" className="mt-[22px]">
            Base URL <span className="font-mono text-t1">{BASE_URL}</span>. Send the key in the <span className="font-mono text-t1">X-ChurchNow-API-Key</span> header. Keys are
            read-only.
          </Banner>
        </>
      )}

      <NewKeyModal
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={(k) => {
          setCreated(k);
          setNewOpen(false);
          void load();
        }}
      />
      <ConfirmDialog
        isOpen={!!revoke}
        onClose={() => setRevoke(null)}
        onConfirm={async () => {
          if (revoke) await act(revoke, 'revoke');
        }}
        type="danger"
        title={revoke ? `Revoke ${revoke.name}?` : 'Revoke key?'}
        message="Requests with this key stop working right away. This can't be undone; create a new key to reconnect."
        confirmText="Revoke key"
      />
    </div>
  );
};

function NewKeyModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (k: ApiKeyCreatedResponse) => void }) {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setName('');
      setError('');
    }
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.churchId) return;
    if (!name.trim()) return setError('Name the integration this key is for');
    setBusy(true);
    try {
      onCreated(await apiKeysApi.generate(user.churchId, name.trim()));
    } catch (err) {
      logger.error('Failed to generate API key', err);
      setError('Could not create the key. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New API key" subtitle="One key per integration, so you can revoke it on its own." width={460}>
      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Field label="Integration name" htmlFor="key-name" error={error}>
          <Input id="key-name" value={name} placeholder="Planning Center sync" invalid={!!error} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <ModalActions onCancel={onClose} confirmLabel="Create key" busy={busy} />
      </form>
    </Modal>
  );
}
